import Image from "@Components/Image";
import { REELS, Reel } from "./reels";
import styles from "./ReelsRail.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * The reel rail under the hero — an auto-running marquee, right to left.
 *
 * It runs on its own rather than waiting to be scrolled: the rail is the page's
 * evidence, and evidence nobody scrolls to does not count. Motion also does the
 * job a set of chevrons would, without asking the reader to work through the
 * rail before they have read the CTA above it.
 *
 * ⚠️  THE LIST IS RENDERED TWICE, and that is the whole mechanism. The track
 * slides by exactly -50% of its own width, at which point copy two sits precisely
 * where copy one began and the animation restarts with nothing visibly jumping.
 * Change the number of copies and the -50% has to change with it.
 *
 * Hovering pauses it. The cards are links to instagram.com, and a target that
 * slides out from under the cursor cannot be clicked.
 */

/**
 * How long one full pass takes, derived from the card count rather than fixed.
 *
 * ⚠️  One pass is the -50% translate, i.e. exactly one copy of `REELS` — so a
 * fixed duration changes the SPEED whenever a reel is added or dropped, and the
 * rail silently drifts. Seconds-per-card keeps the pixel speed constant: slow
 * enough to read a handle as it goes by, at any count.
 */
// 4.4 when the cards were fluid(200, 268); scaled by the same 0.9 when they came
// down 10%, so the rail still travels the same number of pixels per second. Card
// width and this number move together or the speed drifts.
const SECONDS_PER_CARD = 3.96;
const DURATION_S = REELS.length * SECONDS_PER_CARD;

const ReelCard = ({ reel, cloned }: { reel: Reel; cloned?: boolean }) => {
  const { t } = useTranslationHook();

  const hasArt = Boolean(reel.videoSrc || reel.poster);

  return (
    <a
      className={hasArt ? styles.card : `${styles.card} ${styles.cardBare}`}
      href={reel.href}
      target="_blank"
      rel="noreferrer"
      /* The second pass is the same reels. A screen reader should hear them
         once, and tabbing should not land on a duplicate. */
      aria-hidden={cloned ? "true" : undefined}
      tabIndex={cloned ? -1 : undefined}
      /* Only labelled when a handle is known AND the card is a silent image. The
         bare tile carries visible text, which already names the link — an
         aria-label on top of it would just talk over it. */
      aria-label={
        !cloned && hasArt && reel.handle
          ? t("ambassador.v2.reels.cardaria", { handle: reel.handle })
          : undefined
      }
    >
      {reel.videoSrc ? (
        /* Muted, looped, inline, `playsInline` so iOS does not go fullscreen.
           `poster` stays set: it covers the buffer and any failure. */
        <video
          className={styles.poster}
          src={reel.videoSrc}
          poster={reel.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      ) : null}

      {!reel.videoSrc && reel.poster ? (
        <Image className={styles.poster} src={reel.poster} alt="" width="100%" height="100%" />
      ) : null}

      {/* ⚠️  NO ARTWORK, AND THE CARD SAYS SO RATHER THAN BREAKING. Every reel in
          `REELS` currently has a poster, so this branch is a guard, not a state
          the page ships in — it catches a reel added without a still, or a
          poster that 404s off the bucket. The tile is typographic and still goes
          to the right reel. See `reels.ts` for why two supplied reels could not
          be harvested and were dropped instead of shipped through here. */}
      {!hasArt ? (
        <span className={styles.bare}>
          <span className={styles.bareMark} aria-hidden="true">
            {t("ambassador.v2.reels.badge")}
          </span>
          <span className={styles.bareCode} aria-hidden="true">
            {reel.id}
          </span>
          {/* ⚠️  ATTRIBUTION LIVES INSIDE THE TILE HERE, not in `.meta`.
              `.meta` is absolutely positioned so it can sit over a poster; this
              tile is a flow layout anchored to the same bottom edge, so rendering
              both stacked the handle on top of the code. One bottom block per
              card — the poster cards keep `.meta`, this one owns its own. */}
          {reel.handle ? <span className={styles.bareHandle}>@{reel.handle}</span> : null}
          <span className={styles.bareCta}>{t("ambassador.v2.reels.watch")}</span>
        </span>
      ) : null}

      {hasArt ? <span className={styles.scrim} aria-hidden="true" /> : null}

      {/* The attribution strip only exists when there is something true to put in
          it. A card captioned "@undefined" is worse than an uncaptioned card. */}
      {hasArt && reel.handle ? (
        <div className={styles.meta}>
          {reel.avatar ? (
            <Image className={styles.avatar} src={reel.avatar} alt="" width={28} height={28} />
          ) : null}
          <div className={styles.identity}>
            <span className={styles.handle}>@{reel.handle}</span>
            {reel.followers ? (
              <span className={styles.followers}>
                {t("ambassador.v2.reels.followers", { count: reel.followers })}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </a>
  );
};

const ReelsRail = () => {
  const { t } = useTranslationHook();

  return (
    <section className={styles.section} aria-label={t("ambassador.v2.reels.sectionaria")}>
      <div className={styles.viewport}>
        <div className={styles.track} style={{ animationDuration: `${DURATION_S}s` }}>
          {[0, 1].map((pass) =>
            REELS.map((reel) => (
              <ReelCard reel={reel} cloned={pass === 1} key={`${pass}-${reel.id}`} />
            )),
          )}
        </div>
      </div>
    </section>
  );
};

export default wrapperHOC(ReelsRail, {
  componentName: "ReelsRail-AmbassadorV2",
  showForChina: true,
});
